import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';

import {
  errorMessagesActions,
  apiCallFinished,
  withRequiredPrecondition,
  warningModalSelectors,
} from '~/components';
import { appointmentActions, loadClientDetails, updateClientDetails } from '~/api';
import { setEditMode } from '../../edit-appointment.actions';

import {
  YourDetails,
  YOUR_DETAILS_FIELDS as defaultyourDetailsFields,
} from '../../../../about-you/about-you-form/sections';
import { EditButtonsWrapper } from '../../components/edit-buttons-wrapper';

import styles from '../../../../about-you/about-you-form/AboutYouForm.scss';
import { triggerEditPageSaveEditsAnalytics } from '~/analytics/analytics.listener';
import { questionHelpers } from '~/components/questions';
import { restoreAppointmentData } from '~/routes/helpers';
import { setHeadlineBackButtonConfig } from '~/ocab-services/services.actions';

const FORM_NAME = 'AboutYouFormState';

// Custom validation function to replace jl-design-system validate
const validateField = (value, field) => {
  if (!field.validate) return undefined;

  for (const validator of field.validate) {
    // Ensure validator is a function before calling it
    if (typeof validator === 'function') {
      const error = validator(value);
      if (error) return error;
    }
  }
  return undefined;
};

const validateYourDetailsFields = (values, fields) => {
  const errors = {};

  fields.forEach((field) => {
    const error = validateField(values?.[field.name], field);
    if (error) {
      errors[field.name] = error;
    }
  });

  return Object.keys(errors).length > 0 ? errors : undefined;
};

export class EditYourDetails extends PureComponent {
  componentDidMount() {
    const {
      dispatchSetEditMode,
      dispatchApiCallFinished,
      dispatchLoadClientDetails,
      dispatchRestoreAppointment,
      isDisplayQuestions,
    } = this.props;
    dispatchApiCallFinished(false);
    dispatchSetEditMode(true);
    if (!isDisplayQuestions) {
      dispatchLoadClientDetails().then(dispatchApiCallFinished(true));
    } else {
      dispatchRestoreAppointment(false, {}).then(dispatchApiCallFinished(true));
    }
    window.scrollTo(0, 0);
  }

  saveQuestions = (clientDetails) => {
    const {
      questions,

      dispatchUpdateAppointment,
    } = this.props;
    const { email, firstName, lastName, phoneNumber, ...answers } = clientDetails;
    return dispatchUpdateAppointment({
      answers: questionHelpers.getSectionAnswers(questions, answers),
    });
  };

  saveClientDetailsHandler = ({ yourDetailsSection: clientDetails }) => {
    const { history, dispatchApiCallFinished, dispatchSetEditMode, isDisplayQuestions } =
      this.props;
    dispatchApiCallFinished(false);
    dispatchSetEditMode(false);

    return updateClientDetails(clientDetails)
      .then(() => {
        if (isDisplayQuestions) {
          return this.saveQuestions(clientDetails);
        }
        return Promise.resolve();
      })
      .then(() => {
        dispatchApiCallFinished(true);
        triggerEditPageSaveEditsAnalytics('details');
        history.pushWithToken('/review');
      });
  };

  filterByRegisteredFields = (obj) => {
    const { displayedFields } = this.props;
    const yourDetailsFields = Object.keys(displayedFields)
      .filter((field) => field.indexOf('yourDetailsSection') === 0)
      .map((field) => field.replace('yourDetailsSection.', ''));
    const entries = Object.entries(obj).filter(([key]) => yourDetailsFields.includes(key));
    return Object.fromEntries(entries);
  };

  validateForm = (values) => {
    const { yourDetailsFields = defaultyourDetailsFields } = this.props;
    const errors = {};

    const yourDetailsErrors = validateYourDetailsFields(
      values.yourDetailsSection,
      yourDetailsFields,
    );

    if (yourDetailsErrors) {
      errors.yourDetailsSection = yourDetailsErrors;
    }

    return errors;
  };

  onSubmitFail = () => {
    const { dispatchPushSectionErrorToErrorMessage } = this.props;
    dispatchPushSectionErrorToErrorMessage();
  };

  render() {
    const {
      history,
      initialFormState,
      editMode,
      dispatchPushSectionError,
      dispatchClearSectionErrors,
      dispatchSetEditMode,
      location,
      showWarningModal,
      dispatchSetHeadlineBackButtonConfig,
      initialValues,
    } = this.props;

    return (
      <Form
        onSubmit={this.saveClientDetailsHandler}
        initialValues={initialValues}
        validate={this.validateForm}
        keepDirtyOnReinitialize
        render={({ handleSubmit, values }) => {
          const formState = values?.yourDetailsSection || {};

          return (
            <form
              onSubmit={handleSubmit}
              className={styles.AboutYouForm}
              noValidate
              data-testid="EditYourDetailsForm"
            >
              <div className={styles.formWrapper}>
                <YourDetails
                  formName={FORM_NAME}
                  pushSectionError={dispatchPushSectionError}
                  clearSectionErrors={dispatchClearSectionErrors}
                  initialFormValues={initialFormState}
                  editMode={editMode}
                  fieldNamePrefix="yourDetailsSection."
                />
              </div>
              <EditButtonsWrapper
                history={history}
                location={location}
                showWarningModal={showWarningModal}
                temporaryDraftAppointment={this.filterByRegisteredFields(initialFormState)}
                state={this.filterByRegisteredFields(formState)}
                setEditMode={dispatchSetEditMode}
                saveButtonText="Save"
                saveButtonExtraProps={{
                  type: 'submit',
                }}
                setHeadlineBackButtonConfig={dispatchSetHeadlineBackButtonConfig}
              />
            </form>
          );
        }}
      />
    );
  }
}

const getInitialFormState = (form) => {
  let initialFormState = {};
  if (form?.initial?.yourDetailsSection) {
    const { email: initEmail, ...initial } = form.initial.yourDetailsSection;
    initialFormState = initial;
  }
  return initialFormState;
};

export const mapStateToProps = (state) => {
  const {
    client: { details: initialClientDetails },
    form: { AboutYouFormState } = {},
    services: {
      config: { AboutYouForm = {}, questionsSectionsConfig },
    },
    editAppointment: { editMode },
    router: { location },
    questions: {
      questions,
      restoredAnswers: { yourDetailsSection: yourDetailsSectionQuestions },
    },
  } = state;
  const warningModalState = warningModalSelectors.selectState(state);

  const isDisplayQuestions =
    questionsSectionsConfig &&
    questionsSectionsConfig[0] &&
    questionsSectionsConfig[0].displayQuestionsInYourDetails;

  const initialFormState = getInitialFormState(AboutYouFormState);

  return {
    editMode,
    initialValues: {
      yourDetailsSection: {
        ...initialClientDetails,
        ...(isDisplayQuestions ? yourDetailsSectionQuestions : {}),
      },
      termsConditions: AboutYouFormState?.values?.termsConditions,
    },
    yourDetailsFields: AboutYouForm.yourDetailsFields,
    initialFormState,
    displayedFields: AboutYouFormState?.registeredFields || {},
    location,
    showWarningModal: !!warningModalState.isOpen,
    isDisplayQuestions,
    questions,
  };
};

const mapDispatchToProps = {
  dispatchPushSectionError: errorMessagesActions.pushSectionError,
  dispatchClearSectionErrors: errorMessagesActions.clearSectionErrors,
  dispatchPushSectionErrorToErrorMessage: errorMessagesActions.pushSectionErrorToErrorMessage,
  dispatchRestoreAppointment: restoreAppointmentData,
  dispatchApiCallFinished: apiCallFinished,
  dispatchLoadClientDetails: loadClientDetails,
  dispatchSetEditMode: setEditMode,
  dispatchSetHeadlineBackButtonConfig: setHeadlineBackButtonConfig,
  dispatchUpdateAppointment: appointmentActions.updateAppointment,
};

export const ConnectedEditYourDetails = withRequiredPrecondition(
  connect(mapStateToProps, mapDispatchToProps)(EditYourDetails),
);

// Keep the old export name for backwards compatibility
export const ConnectedEditYourDetailsReduxForm = ConnectedEditYourDetails;

EditYourDetails.propTypes = {
  dispatchPushSectionError: PropTypes.func.isRequired,
  dispatchClearSectionErrors: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchLoadClientDetails: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchRestoreAppointment: PropTypes.func.isRequired,
  dispatchPushSectionErrorToErrorMessage: PropTypes.func.isRequired,
  history: PropTypes.shape({
    pushWithToken: PropTypes.func,
  }).isRequired,
  initialFormState: PropTypes.shape({}).isRequired,
  editMode: PropTypes.bool.isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  location: PropTypes.shape({}).isRequired,
  questions: PropTypes.shape({}).isRequired,
  isDisplayQuestions: PropTypes.bool,
  dispatchSetHeadlineBackButtonConfig: PropTypes.func.isRequired,
  dispatchUpdateAppointment: PropTypes.func.isRequired,
  displayedFields: PropTypes.shape({}).isRequired,
  initialValues: PropTypes.shape({}).isRequired,
  yourDetailsFields: PropTypes.array,
};

EditYourDetails.defaultProps = {
  isDisplayQuestions: false,
};
